import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { compose } from "redux";

import { smartTopRightTextColors, analytics } from "rtr-constants";
import ActionLogger from "action-logger";
import { Referrals } from "routes";
import RtrImage from "../shared/rtr-image";
import styles from "./top-right.module.scss";
import HeapHelpers from "helpers/heap-helpers";
import { isIdentified } from "../hoc/with-user-data";
import {
  flagsAndExperimentsPropType,
  flagsAndExperimentNames,
  withOnLoadFlagsAndExperiments,
} from "components/source/hoc/with-flags-and-experiments";
import { useSubscriptionRejoinUrl } from "hooks/use-subscription-rejoin-url";
import { useAppSelector } from "../../../../../util/store-types";

function TopRight({ flagsAndExperiments, flagsAndExperimentsLoaded }) {
  const [experimentReady, setExperimentReady] = useState(false);
  const { userCanRejoin, rejoinUrl } = useSubscriptionRejoinUrl();

  const userData = useAppSelector(state => state.userData);
  const membershipState = useAppSelector(state => state.membershipState);
  const didFetchSmartTopRight = useAppSelector(state => state.smartComponentState?.didFetchSmartTopRight);
  const smartTopRightCMS = useAppSelector(state => state.smartComponentState?.smartTopRightCMS);

  useEffect(() => {
    if (
      flagsAndExperimentsLoaded &&
      userData?.userProfileHasLoaded &&
      !experimentReady &&
      isIdentified(userData) &&
      !userData?.userProfile?.isCustomer
    ) {
      setExperimentReady(true);
      HeapHelpers.fireHeapEvent("top-right-nco-experiment", {
        isExperiment:
          flagsAndExperiments?.[flagsAndExperimentNames.RSV_1198_NCO_TOP_RIGHT_CHANGE_TEXT_FOR_RESERVE_USERS],
      });
    }
  }, [
    flagsAndExperimentsLoaded,
    userData?.userProfileHasLoaded,
    userData?.userProfile?.isCustomer,
    flagsAndExperiments?.[flagsAndExperimentNames.RSV_1198_NCO_TOP_RIGHT_CHANGE_TEXT_FOR_RESERVE_USERS],
    experimentReady,
  ]);

  const isEligibleForPromo = useCallback(() => {
    const lens = userData?.isClassicLens;

    return (
      flagsAndExperiments?.[flagsAndExperimentNames?.RSV_1198_NCO_TOP_RIGHT_CHANGE_TEXT_FOR_RESERVE_USERS] &&
      !userData?.userProfile?.isCustomer &&
      lens &&
      isIdentified(userData) &&
      experimentReady
    );
  }, [flagsAndExperiments, userData, experimentReady]);

  const logImageBanner = useCallback(() => {
    const link = smartTopRightCMS?.userMenu?.link;
    if (link === Referrals.inviteAFriend) {
      ActionLogger.inferAction({
        object_type: analytics.OBJECT_TYPE.TOP_NAV,
        action: analytics.ACTION_TYPE.CLICK_REFERRAL_BANNER,
        term: membershipState?.membershipTermNumber,
        membership_id: membershipState?.membershipId,
        user_id: membershipState?.id,
      });
    }
    if (isEligibleForPromo()) {
      HeapHelpers.fireHeapEvent("top-right-nco-selected", { componentName: "top-right" });
    }
  }, [smartTopRightCMS, membershipState, isEligibleForPromo]);

  const renderPromoOffer = useCallback(
    (userMenu = {}) => {
      const baseMenu = {
        ...userMenu,
        ...(isEligibleForPromo()
          ? {
              title: `<em>20% OFF FIRST RENTAL</em>`,
              subTitle: "CODE: GETDRESSED",
              link: "/shop/event_rentals/products",
              text: "20% OFF FIRST RENTAL CODE: GETDRESSED",
              textColor: "light",
              fontFamily: "proxima-nova",
              borderColor: "#1C1C1C",
              backgroundColor: "#1C1C1C",
              color: "#FFFFFF",
            }
          : {}),
      };

      // adjust link for churners, but swallow any "read-only property" errors
      if (smartTopRightCMS?.name === "churner") {
        baseMenu.link = userCanRejoin && rejoinUrl ? rejoinUrl : smartTopRightCMS.userMenu?.link;
      }

      const {
        title,
        subTitle,
        imageUrl,
        retinaImage,
        link,
        width,
        backgroundColor,
        borderColor,
        textColor,
        fontFamily,
        type,
        altText,
      } = baseMenu;
      const isTailwindNav = flagsAndExperiments[flagsAndExperimentNames.CATALOG_SERVICES_ENABLE_TAILWIND_NAV];

      const wrapperStyles = {};
      if (width && !isTailwindNav) {
        wrapperStyles.width = `${width}px`;
        wrapperStyles.maxWidth = "none";
        wrapperStyles.minWidth = "none";
      }
      if (borderColor) wrapperStyles.borderColor = borderColor;
      if (backgroundColor) wrapperStyles.backgroundColor = backgroundColor;
      if (textColor) wrapperStyles.color = smartTopRightTextColors[textColor] || textColor;

      if (title) {
        const offerWrapperClass = classNames("offer-wrapper", {
          [`offer-wrapper--${fontFamily}`]: fontFamily,
          "offer-wrapper--button": type === "button",
          "rounded-full w-full !max-w-none !mr-none flex-row md:flex-col justify-center !text-sm pl-3 pr-3":
            isTailwindNav,
        });

        const handleClick = () => {
          if (isEligibleForPromo()) {
            HeapHelpers.fireHeapEvent("top-right-nco-selected", { componentName: "top-right" });
          }
        };

        return (
          <a href={link || "#"} className={offerWrapperClass} style={wrapperStyles} onClick={handleClick}>
            <div
              className={`offer-text ${isTailwindNav && "!normal-case"}`}
              dangerouslySetInnerHTML={{ __html: title }}
            />
            {isTailwindNav && <div className="pl-3 pr-3 md:hidden">|</div>}
            <div
              className={`subtitle ${isTailwindNav && "!normal-case"}`}
              dangerouslySetInnerHTML={{ __html: subTitle || "" }}
            />
          </a>
        );
      }

      const image = retinaImage || imageUrl;
      const retinaModifier = retinaImage ? "offer-wrapper--retina-image" : "";
      return (
        <a
          href={link || "#"}
          className={`offer-wrapper offer-wrapper--image ${retinaModifier}`}
          style={wrapperStyles}
          onClick={logImageBanner}>
          <RtrImage src={image} alt={altText || ""} />
        </a>
      );
    },
    [isEligibleForPromo, smartTopRightCMS, membershipState, flagsAndExperiments, logImageBanner]
  );

  if (!didFetchSmartTopRight || !userData?.userProfileHasLoaded || !flagsAndExperimentsLoaded) {
    return (
      <div data-test-id="shimmer" className={styles.shimmerWrapper}>
        <div className={styles.block1} />
        <div className={styles.block2} />
        <div className={styles.animation} />
      </div>
    );
  }
  if (!smartTopRightCMS) {
    return <div data-test-id="empty-space" className={styles.emptySpace} />;
  }

  return renderPromoOffer(smartTopRightCMS.userMenu || {});
}

TopRight.propTypes = {
  flagsAndExperiments: flagsAndExperimentsPropType,
  flagsAndExperimentsLoaded: PropTypes.bool,
};

export const TopRightBannerComponent = compose(
  withOnLoadFlagsAndExperiments(
    flagsAndExperimentNames.RSV_1198_NCO_TOP_RIGHT_CHANGE_TEXT_FOR_RESERVE_USERS,
    flagsAndExperimentNames.CATALOG_SERVICES_ENABLE_TAILWIND_NAV
  )
)(TopRight);

export default TopRightBannerComponent;
export { TopRight as UnconnectedTopRight };
